import React, { useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  API,
  showError,
  showInfo
} from '../../helpers';

import {
  Breadcrumb,
  Button,
  Container,
  Dropdown,
  Grid,
  Header,
  Icon,
  Label,
  List,
  Loader,
  Message,
  Segment
} from 'semantic-ui-react';

import FileViewer from '../../components/FileViewer';
import { StatusContext } from '../../context/Status';

// 获取文件类型图标
function getFileIcon(fileType, isDir) {
  if (isDir) {
    return 'folder';
  }
  
  switch (fileType) {
    case 'javascript':
      return 'js square';
    case 'typescript':
      return 'code';
    case 'python':
      return 'python';
    case 'go':
      return 'code';
    case 'java':
      return 'java';
    case 'html':
      return 'html5';
    case 'css':
      return 'css3';
    case 'json':
      return 'file code';
    case 'markdown':
      return 'markdown';
    case 'image':
      return 'image';
    case 'pdf':
      return 'file pdf';
    case 'archive':
      return 'archive';
    default:
      return 'file';
  }
}

// 格式化文件大小
function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

const RepositoryBrowser = () => {
  const { namespace, name } = useParams();
  const fullName = namespace && name ? `${namespace}/${name}` : name;
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusState] = useContext(StatusContext);
  
  const [loading, setLoading] = useState(true);
  const [fileLoading, setFileLoading] = useState(false);
  const [currentPath, setCurrentPath] = useState(searchParams.get('path') || '');
  const [currentRef, setCurrentRef] = useState(searchParams.get('ref') || 'HEAD');
  const [entries, setEntries] = useState([]);
  const [refs, setRefs] = useState({ branches: [], tags: [], default_branch: '' });
  const [currentFile, setCurrentFile] = useState(null);
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'blob'
  
  // 判断 Git 功能是否启用
  const gitEnabled = statusState?.status?.git_enabled !== false;

  // 如果 Git 功能被禁用，显示提示信息
  if (!gitEnabled) {
    return (
      <Container style={{ marginTop: '2em' }}>
        <Message warning icon>
          <Icon name='warning sign' />
          <Message.Content>
            <Message.Header>Git 功能未启用</Message.Header>
            <p>当前系统配置中 Git 功能已被禁用。如需使用 Git 仓库浏览功能，请联系系统管理员启用 GIT_ENABLED 配置项。</p>
          </Message.Content>
        </Message>
      </Container>
    );
  }

  // 加载引用列表
  const loadRefs = async () => {
    try {
      const res = await API.get(`/api/admin/git/repository/${fullName}?action=refs`);
      const { success, data, message } = res.data;
      if (success) {
        setRefs(data);
        // 处理空仓库的情况
        if (data.is_empty) {
          setEntries([]);
          setCurrentRef('');
          setViewMode('empty');
          return;
        }
        if (currentRef === 'HEAD' && data.default_branch) {
          setCurrentRef(data.default_branch);
        }
      } else {
        showError(message || '获取分支列表失败');
      }
    } catch (error) {
      showError('获取分支列表失败');
    }
  };

  // 加载文件树
  const loadTree = async (path = '', ref = currentRef) => {
    setLoading(true);
    try {
      const res = await API.get(`/api/admin/git/repository/${fullName}?action=tree&path=${path}&ref=${ref}`);
      const { success, data, message } = res.data;
      if (success) {
        // 处理空仓库的情况
        if (data.is_empty) {
          setEntries([]);
          setCurrentPath(path);
          setCurrentRef(ref);
          setViewMode('empty');
          setCurrentFile(null);
          
          // 更新URL参数
          const newParams = new URLSearchParams();
          if (path) newParams.set('path', path);
          if (ref !== 'HEAD') newParams.set('ref', ref);
          setSearchParams(newParams);
          setLoading(false);
          return;
        }
        
        setEntries(data.entries || []);
        setCurrentPath(path);
        setCurrentRef(ref);
        setViewMode('tree');
        setCurrentFile(null);
        
        // 更新URL参数
        const newParams = new URLSearchParams();
        if (path) newParams.set('path', path);
        if (ref !== 'HEAD') newParams.set('ref', ref);
        setSearchParams(newParams);
      } else {
        showError(message || '获取文件列表失败');
      }
    } catch (error) {
      showError('获取文件列表失败');
    }
    setLoading(false);
  };

  // 加载文件内容
  const loadFile = async (filePath, ref = currentRef) => {
    setFileLoading(true);
    try {
      const res = await API.get(`/api/admin/git/repository/${fullName}?action=blob&path=${filePath}&ref=${ref}`);
      const { success, data, message } = res.data;
      if (success) {
        setCurrentFile(data);
        setViewMode('blob');
        setCurrentPath(filePath);
        setCurrentRef(ref);
        
        // 更新URL参数
        const newParams = new URLSearchParams();
        newParams.set('path', filePath);
        newParams.set('view', 'blob');
        if (ref !== 'HEAD') newParams.set('ref', ref);
        setSearchParams(newParams);
      } else {
        showError(message || '获取文件内容失败');
      }
    } catch (error) {
      showError('获取文件内容失败');
    }
    setFileLoading(false);
  };

  // 处理文件/文件夹点击
  const handleItemClick = (item) => {
    if (item.is_dir) {
      const newPath = currentPath ? `${currentPath}/${item.name}` : item.name;
      loadTree(newPath);
    } else {
      const filePath = currentPath ? `${currentPath}/${item.name}` : item.name;
      loadFile(filePath);
    }
  };

  // 处理面包屑导航
  const handleBreadcrumbClick = (path) => {
    if (path === '') {
      loadTree('');
    } else {
      loadTree(path);
    }
  };

  // 处理分支切换
  const handleRefChange = (newRef) => {
    if (viewMode === 'blob') {
      loadFile(currentPath, newRef);
    } else {
      loadTree(currentPath, newRef);
    }
  };

  // 生成面包屑
  const generateBreadcrumbs = () => {
    if (!currentPath) {
      return [{ name: name, path: '' }];
    }
    
    const parts = currentPath.split('/');
    const breadcrumbs = [{ name: name, path: '' }];
    
    let currentBreadcrumbPath = '';
    parts.forEach((part, index) => {
      currentBreadcrumbPath += (index > 0 ? '/' : '') + part;
      breadcrumbs.push({
        name: part,
        path: currentBreadcrumbPath
      });
    });
    
    return breadcrumbs;
  };

  // 生成引用选项
  const generateRefOptions = () => {
    const options = [];
    
    if (refs.branches.length > 0) {
      options.push({
        key: 'branches-header',
        text: '分支',
        disabled: true,
        content: <Header size='tiny'>分支</Header>
      });
      
      refs.branches.forEach(branch => {
        options.push({
          key: `branch-${branch.name}`,
          text: branch.name,
          value: branch.name,
          content: (
            <div>
              <Icon name='code branch' />
              {branch.name}
              {branch.is_default && <Label size='mini' color='blue'>默认</Label>}
            </div>
          )
        });
      });
    }
    
    if (refs.tags.length > 0) {
      options.push({
        key: 'tags-header',
        text: '标签',
        disabled: true,
        content: <Header size='tiny'>标签</Header>
      });
      
      refs.tags.forEach(tag => {
        options.push({
          key: `tag-${tag.name}`,
          text: tag.name,
          value: tag.name,
          content: (
            <div>
              <Icon name='tag' />
              {tag.name}
            </div>
          )
        });
      });
    }
    
    return options;
  };

  useEffect(() => {
    const initPath = searchParams.get('path') || '';
    const initRef = searchParams.get('ref') || 'HEAD';
    const initView = searchParams.get('view') || 'tree';
    
    loadRefs();
    
    if (initView === 'blob' && initPath) {
      loadFile(initPath, initRef);
    } else {
      loadTree(initPath, initRef);
    }
  }, [name]);

  const breadcrumbs = generateBreadcrumbs();
  const refOptions = generateRefOptions();

  return (
    <Container style={{ marginTop: '20px' }}>
      <Grid>
        <Grid.Row>
          <Grid.Column>
            <Header as='h2'>
              <Icon name='github' />
              <Header.Content>
                {fullName}
                <Header.Subheader>仓库浏览</Header.Subheader>
              </Header.Content>
            </Header>
          </Grid.Column>
        </Grid.Row>
        
        {/* 分支选择器和面包屑 */}
        <Grid.Row>
          <Grid.Column width={4}>
            <Dropdown
              placeholder='选择分支或标签'
              fluid
              selection
              value={currentRef}
              onChange={(e, { value }) => handleRefChange(value)}
              options={refOptions}
              loading={!refs.branches.length && !refs.tags.length}
            />
          </Grid.Column>
          <Grid.Column width={12}>
            <Breadcrumb size='large'>
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <Breadcrumb.Divider icon='right angle' />}
                  <Breadcrumb.Section
                    link={index < breadcrumbs.length - 1}
                    onClick={() => index < breadcrumbs.length - 1 && handleBreadcrumbClick(crumb.path)}
                  >
                    {index === 0 ? <Icon name='home' /> : null}
                    {crumb.name}
                  </Breadcrumb.Section>
                </React.Fragment>
              ))}
            </Breadcrumb>
          </Grid.Column>
        </Grid.Row>

        {/* 内容区域 */}
        <Grid.Row>
          <Grid.Column>
            {viewMode === 'empty' ? (
              <Segment placeholder>
                <Header icon>
                  <Icon name='github' />
                  仓库为空
                </Header>
                <Message info>
                  <Message.Header>这是一个空仓库</Message.Header>
                  <p>该仓库还没有任何提交。您可以通过以下方式开始：</p>
                  <Message.List>
                    <Message.Item>通过Git客户端推送代码到这个仓库</Message.Item>
                    <Message.Item>使用命令行工具进行首次提交</Message.Item>
                  </Message.List>
                </Message>
              </Segment>
            ) : viewMode === 'tree' ? (
              <Segment>
                {loading ? (
                  <Loader active inline='centered'>加载中...</Loader>
                ) : entries.length === 0 ? (
                  <Message info>
                    <Icon name='info circle' />
                    此目录为空
                  </Message>
                ) : (
                  <List divided relaxed>
                    {/* 返回上级目录 */}
                    {currentPath && (
                      <List.Item
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          const parentPath = currentPath.split('/').slice(0, -1).join('/');
                          handleBreadcrumbClick(parentPath);
                        }}
                      >
                        <Icon name='level up alternate' size='large' style={{ color: '#999' }} />
                        <List.Content>
                          <List.Header style={{ color: '#999' }}>...</List.Header>
                        </List.Content>
                      </List.Item>
                    )}
                    
                    {entries.map((entry, index) => (
                      <List.Item
                        key={index}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleItemClick(entry)}
                      >
                        <Icon 
                          name={getFileIcon(entry.type, entry.is_dir)} 
                          size='large'
                          color={entry.is_dir ? 'blue' : 'grey'}
                        />
                        <List.Content>
                          <List.Header>{entry.name}</List.Header>
                          <List.Description>
                            {entry.is_dir ? '目录' : formatFileSize(entry.size)}
                          </List.Description>
                        </List.Content>
                      </List.Item>
                    ))}
                  </List>
                )}
              </Segment>
            ) : (
              <div>
                {fileLoading ? (
                  <Segment>
                    <Loader active inline='centered'>加载文件内容...</Loader>
                  </Segment>
                ) : currentFile ? (
                  <FileViewer 
                    file={currentFile} 
                    onBack={() => {
                      const parentPath = currentPath.split('/').slice(0, -1).join('/');
                      loadTree(parentPath);
                    }}
                  />
                ) : null}
              </div>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Container>
  );
};

export default RepositoryBrowser; 